import { ref, computed } from 'vue';
import slotsConfigAPI from '../api/omni/slotsConfig';
import { useAlert } from 'dashboard/composables';
import CalendarAPI from '../api/calendar';
import WaCatalogAPI from '../api/omni/waCatalog';

const MAX_FILE_SIZE = 40 * 1024 * 1024; // 40MB
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'video/mp4',
];

export function useSlotsConfig() {
  const slots = ref([]);
  const bots = ref([]);
  const calendars = ref([]);
  const catalogs = ref([]);
  const loading = ref(false);
  const currentSlot = ref(null);

  const slotsByBot = computed(() => {
    const grouped = {};
    slots.value.forEach(slot => {
      const botId = slot.agent_bot_id;
      if (!grouped[botId]) {
        grouped[botId] = [];
      }
      grouped[botId].push(slot);
    });
    return grouped;
  });

  const requiredSlots = computed(() => {
    return slots.value.filter(s => s.is_required);
  });

  const fetchSlots = async (agentBotId = null) => {
    loading.value = true;
    try {
      const params = agentBotId ? { agent_bot_id: agentBotId } : {};
      const response = await slotsConfigAPI.get(params);
      slots.value = response.data || [];
    } catch (error) {
      useAlert(error.message || 'Error fetching slots');
    } finally {
      loading.value = false;
    }
  };

  const fetchBots = async () => {
    try {
      const response = await slotsConfigAPI.getBots();
      bots.value = response.data || [];
    } catch (error) {
      useAlert(error.message || 'Error fetching bots');
    }
  };

  const fetchCalendars = async () => {
    try {
      const response = await CalendarAPI.getCalendars();
      calendars.value = response.data || [];
    } catch (error) {
      useAlert(error.message || 'Error fetching calendars');
    }
  };

  const fetchCatalogs = async () => {
    try {
      const response = await WaCatalogAPI.getCatalogs();
      catalogs.value = response.data || [];
    } catch {
      catalogs.value = [];
    }
  };

  const buildFormData = (slotData, actionFile) => {
    const formData = new FormData();
    Object.entries(slotData).forEach(([key, value]) => {
      if (key === 'action_config' && typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
    if (actionFile) {
      formData.append('action_file', actionFile);
    }
    return formData;
  };

  const validateFile = file => {
    if (!file) return null;
    if (file.size > MAX_FILE_SIZE) {
      return 'FILE_SIZE_EXCEEDED';
    }
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return 'FILE_TYPE_NOT_ALLOWED';
    }
    return null;
  };

  const createSlot = async (slotData, actionFile = null) => {
    loading.value = true;
    try {
      const data =
        actionFile || slotData.action_type === 'send_file'
          ? buildFormData(slotData, actionFile)
          : slotData;
      const response = await slotsConfigAPI.create(data);
      slots.value.push(response.data);
      useAlert('Slot created successfully', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error creating slot');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const updateSlot = async (id, slotData, actionFile = null) => {
    loading.value = true;
    try {
      const data =
        actionFile || slotData.action_type === 'send_file'
          ? buildFormData(slotData, actionFile)
          : slotData;
      const response = await slotsConfigAPI.update(id, data);
      const index = slots.value.findIndex(s => s.id === id);
      if (index !== -1) {
        slots.value[index] = response.data;
      }
      useAlert('Slot updated successfully', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error updating slot');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const removeSlotFile = async id => {
    try {
      const response = await slotsConfigAPI.removeFile(id);
      const index = slots.value.findIndex(s => s.id === id);
      if (index !== -1) {
        slots.value[index] = response.data;
      }
      useAlert('File removed successfully', 'success');
      return response.data;
    } catch (error) {
      useAlert(error.message || 'Error removing file');
      throw error;
    }
  };

  const deleteSlot = async id => {
    loading.value = true;
    try {
      await slotsConfigAPI.delete(id);
      slots.value = slots.value.filter(s => s.id !== id);
      useAlert('Slot deleted successfully', 'success');
    } catch (error) {
      useAlert(error.message || 'Error deleting slot');
      throw error;
    } finally {
      loading.value = false;
    }
  };

  return {
    slots,
    bots,
    calendars,
    catalogs,
    loading,
    currentSlot,
    slotsByBot,
    requiredSlots,
    fetchSlots,
    fetchBots,
    fetchCalendars,
    fetchCatalogs,
    createSlot,
    updateSlot,
    deleteSlot,
    removeSlotFile,
    validateFile,
  };
}

